import React, { useEffect, useRef, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import LanguageSelector from '../LanguageSelector';
import NotificationBell from '../NotificationBell';
import HomeDashboardDropdown from '../HomeDashboardDropdown';
import { canSelectDashboard } from '../../utils/dashboardAccess';
import { isAdmin as checkAdmin, getUserRoles, canAccess } from '../../utils/roleUtils';
import './Header.css';

const Header = ({ currentPage, onPageChange }) => {
  const { t } = useTranslation('common');
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const headerNavRef = useRef(null);
  const isHeaderFocused = useRef(false);

  // Dashboard dropdown state
  const [showDashboardDropdown, setShowDashboardDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const homeButtonRef = useRef(null);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Handle home button click - show dropdown for eligible users
  const handleHomeButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (canSelectDashboard(getUserRoles(user))) {
      // Calculate dropdown position
      const buttonRect = homeButtonRef.current?.getBoundingClientRect();
      if (buttonRect) {
        setDropdownPosition({
          top: buttonRect.bottom + 8, // 8px below button
          left: buttonRect.left
        });
      }
      setShowDashboardDropdown(true);
    } else {
      // For non-eligible users, navigate directly to home
      onPageChange('home');
    }
  };

  // Close dashboard dropdown
  const closeDashboardDropdown = () => {
    setShowDashboardDropdown(false);
  };


  const currentTime = new Date().toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit'
  });

  const navigationItems = useMemo(() => [
    { key: 'F1', id: 'home',        label: t('navigation.home') },
    { key: 'F2', id: 'production',  label: t('navigation.production') },
    { key: 'F3', id: 'products',    label: t('navigation.products') },
    { key: 'F4', id: 'sales',       label: t('navigation.sales') },
    { key: 'F5', id: 'procurement', label: t('navigation.procurement') },
    { key: 'F6', id: 'inventory',   label: t('navigation.inventory') },
    { key: 'F7', id: 'admin',       label: t('navigation.admin') },
    { key: 'F8', id: 'ai',          label: t('navigation.ai') },
    { key: 'F9', id: 'docs',        label: t('navigation.docs'), external: '/docs-user' },
    { key: 'F10', id: 'qc',         label: t('navigation.qc') },
  ].filter(item => item.external || canAccess(user, item.id)), [t, user]);

  // Add keyboard navigation for main pages
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Don't trigger if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      // Check if a grid, tab bar or other focusable element has focus
      const focusedElement = document.activeElement;
      const isGridFocused = focusedElement && focusedElement.classList.contains('grid-table-container');
      const isTabFocused = focusedElement && focusedElement.classList.contains('tab-button');

      // Only handle navigation at header level when appropriate
      if (!isGridFocused && !isTabFocused) {
        const currentIndex = navigationItems.findIndex(item => item.id === currentPage);

        // Ctrl/Cmd + Arrow for main navigation
        if ((e.ctrlKey || e.metaKey) && e.key === 'ArrowLeft') {
          e.preventDefault();
          e.stopPropagation();
          // Move to previous page
          if (currentIndex > 0) {
            onPageChange(navigationItems[currentIndex - 1].id);
            isHeaderFocused.current = true;
          }
          return;
        } else if ((e.ctrlKey || e.metaKey) && e.key === 'ArrowRight') {
          e.preventDefault();
          e.stopPropagation();
          // Move to next page
          if (currentIndex < navigationItems.length - 1) {
            onPageChange(navigationItems[currentIndex + 1].id);
            isHeaderFocused.current = true;
          }
          return;
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentPage, onPageChange, navigationItems]);

  return (
    <header className="terminal-header">
      <div className="header-left">
        <span className="header-logo">MPS ERP v0.1.0</span>
      </div>

      <div className="header-center">
        <div className="header-nav-items">
          {navigationItems.map((item) => (
            <button
              key={item.id}
              ref={item.id === 'home' ? homeButtonRef : null}
              className={`header-nav-item ${currentPage === item.id ? 'active' : ''}`}
              onClick={item.id === 'home' ? handleHomeButtonClick : item.external ? () => window.open(item.external, '_blank') : () => onPageChange(item.id)}
              title={`${item.key} - ${item.label}`}
            >
              <span className="nav-key">{item.key}</span>
              <span className="nav-label">{item.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="header-right">
        <NotificationBell />
        <span className="header-separator">│</span>
        <span className="header-user">{user?.username || user?.full_name || t('userInfo.defaultUser')}</span>
        <span className="header-separator">│</span>
        <span className="header-time">{currentTime}</span>
        <span className="header-separator">│</span>
        <button
          type="button"
          className="theme-toggle"
          onClick={toggleTheme}
          title={theme === 'dark' ? t('header.switchToLight') : t('header.switchToDark')}
          aria-label={theme === 'dark' ? t('header.switchToLight') : t('header.switchToDark')}
        >
          {theme === 'dark' ? '☀' : '☾'}
        </button>
        <span className="header-separator">│</span>
        <LanguageSelector compact={true} />
        <span className="header-separator">│</span>
        <button
          type="button"
          className="header-logout"
          onClick={handleLogout}
          title={t('actions.logout')}
        >
          {t('actions.logout')}
        </button>
      </div>

      {/* Dashboard selection dropdown */}
      <HomeDashboardDropdown
        isOpen={showDashboardDropdown}
        onClose={closeDashboardDropdown}
        position={dropdownPosition}
      />
    </header>
  );
};

export default Header;